import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Score } from '@sorare/core/src/components/scoring/Score';
import { Captain } from '@sorare/core/src/components/so5/Captain';
import { isType } from '@sorare/core/src/lib/gql';
import type { Props as BriefPlayerScoreProps } from '@sorare/marketplace/src/shared/stats/BriefPlayerScore';
import { BriefPlayerScore } from '@sorare/marketplace/src/shared/stats/BriefPlayerScore';

import { So5AppearanceScore } from '#src/components/appearance/So5AppearanceScore/index.tsx';

import type { BriefSo5AppearanceScore_so5Appearance } from './__generated__/index.graphql.ts';

const CaptainWrapper = styled.div`
  position: absolute;
  top: -6px;
  left: -6px;
`;

type Props = { so5Appearance: BriefSo5AppearanceScore_so5Appearance } & Pick<
  BriefPlayerScoreProps,
  'disableLinks'
>;

export const BriefSo5AppearanceScore = ({ so5Appearance, ...rest }: Props) => {
  const {
    anyPlayer,
    eligiblePlayerGameScores,
    pictureUrl,
    scoreStatus,
    scoreWithoutBonus,
    bonus,
    captain,
  } = so5Appearance;

  if (
    !isType(so5Appearance, 'So5Appearance') &&
    !isType(so5Appearance, 'ThresholdPickerTaskAppearance')
  ) {
    return null;
  }

  return (
    <BriefPlayerScore
      {...rest}
      anyCard={
        isType(so5Appearance, 'So5Appearance')
          ? so5Appearance.anyCard
          : so5Appearance.card
      }
      cardBadge={
        !!captain && (
          <CaptainWrapper>
            <Captain active size={2} />
          </CaptainWrapper>
        )
      }
      anyPlayer={anyPlayer}
      playerGameScores={eligiblePlayerGameScores}
      pictureUrl={pictureUrl || ''}
      bonus={bonus}
      scoreWithoutBonus={
        <Score
          shape="PILL"
          scoreType="PLAYER_SCORE"
          score={scoreWithoutBonus}
          scoreStatus={scoreStatus}
        />
      }
    >
      <So5AppearanceScore so5Appearance={so5Appearance} withBonus />
    </BriefPlayerScore>
  );
};

BriefSo5AppearanceScore.fragments = {
  so5Appearance: gql`
    fragment BriefSo5AppearanceScore_so5Appearance on AppearanceInterface {
      id
      captain
      eligiblePlayerGameScores {
        id
        ...BriefPlayerScore_playerGameScoreInterface
      }
      scoreWithoutBonus: score(withBonus: false)
      scoreStatus
      pictureUrl
      bonus
      anyPlayer {
        slug
        ...BriefPlayerScore_anyPlayerInterface
      }
      ... on So5Appearance {
        id
        anyCard {
          slug
          ...BriefPlayerScore_anyCardInterface
        }
      }
      ... on ThresholdPickerTaskAppearance {
        id
        card {
          slug
          ...BriefPlayerScore_anyCardInterface
        }
      }
      ...So5AppearanceScore_so5Appearance
    }
    ${BriefPlayerScore.fragments.anyCardInterface}
    ${BriefPlayerScore.fragments.anyPlayerInterface}
    ${BriefPlayerScore.fragments.playerGameScoreInterface}
    ${So5AppearanceScore.fragments.so5Appearance}
  ` as TypedDocumentNode<BriefSo5AppearanceScore_so5Appearance>,
};
